import { useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, Loader2, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiError } from "../lib/auth";

const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // If a ProtectedRoute redirected us here, send the user back on success.
  const redirectTo = location.state?.from || "/dashboard";

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
    marketing_consent: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const passwordOk = useMemo(() => PASSWORD_RULE.test(form.password), [form.password]);
  const passwordsMatch = form.password && form.password === form.confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordOk) {
      setError("Password must be at least 8 characters and include a letter and a number.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        password: form.password,
        marketing_consent: form.marketing_consent,
      });
      toast.success("Account created — welcome to CV Folks.");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg = formatApiError(err, "Could not create your account.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-muted/30 min-h-[80vh] py-16 md:py-24">
        <div className="max-w-md mx-auto px-6">
          <div
            data-testid="register-card"
            className="bg-card rounded-3xl border border-border p-8 md:p-10 shadow-sm"
          >
            <p className="label-eyebrow">Get started</p>
            <h1 className="font-serif text-3xl md:text-4xl font-bold mt-2">
              Create your account
            </h1>
            <p className="text-sm text-foreground/70 mt-2">
              Save your CVs, track orders and unlock the AI CV Builder.
            </p>

            <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
              <div>
                <Label htmlFor="reg-name">Full name</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reg-name"
                    autoComplete="name"
                    data-testid="register-name"
                    value={form.full_name}
                    onChange={change("full_name")}
                    className="h-12 pl-10"
                    placeholder="Jane Doe"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reg-email">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reg-email"
                    type="email"
                    autoComplete="email"
                    data-testid="register-email"
                    value={form.email}
                    onChange={change("email")}
                    className="h-12 pl-10"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reg-phone">Phone (optional)</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reg-phone"
                    type="tel"
                    autoComplete="tel"
                    data-testid="register-phone"
                    value={form.phone}
                    onChange={change("phone")}
                    className="h-12 pl-10"
                    placeholder="+44 7..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="reg-password">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reg-password"
                    type="password"
                    autoComplete="new-password"
                    data-testid="register-password"
                    value={form.password}
                    onChange={change("password")}
                    className="h-12 pl-10"
                    placeholder="At least 8 characters"
                    required
                  />
                </div>
                <p
                  className={`text-xs mt-1 ${
                    form.password ? (passwordOk ? "text-emerald-600" : "text-destructive") : "text-muted-foreground"
                  }`}
                >
                  8+ characters, including at least 1 letter and 1 number.
                </p>
              </div>

              <div>
                <Label htmlFor="reg-confirm">Confirm password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="reg-confirm"
                    type="password"
                    autoComplete="new-password"
                    data-testid="register-confirm"
                    value={form.confirm}
                    onChange={change("confirm")}
                    className="h-12 pl-10"
                    placeholder="Repeat your password"
                    required
                  />
                </div>
              </div>

              <label className="flex items-start gap-3 text-sm text-foreground/80 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="register-marketing-consent"
                  checked={form.marketing_consent}
                  onChange={change("marketing_consent")}
                  className="mt-1 h-4 w-4 accent-brand"
                />
                <span>
                  Email me occasional CV tips and offers. Unsubscribe anytime.
                </span>
              </label>

              {error && (
                <p
                  data-testid="register-error"
                  className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="register-submit"
                disabled={submitting}
                className="btn-primary w-full h-12 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {submitting ? "Creating account..." : "Create account"}
              </button>
            </form>

            <p className="text-sm text-foreground/70 mt-6 text-center">
              Already have an account?{" "}
              <Link
                to="/login"
                data-testid="register-to-login"
                className="text-brand font-semibold hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
